import React from 'react';
import { ContractNote } from '@/types/client';
import { Pill, SideTag } from '@/components/ui';
import shared from '@/components/utils/shared.module.css';
import styles from './ContractNotesTable.module.css';

interface ContractNotesTableProps {
    notes: ContractNote[];
    /** Adds the leading Sr. No. and allocation Status columns (Manage Files). */
    showAllocation?: boolean;
}

const NUMERIC_COLUMNS: { header: string; value: (n: ContractNote) => React.ReactNode }[] = [
    { header: 'Qty', value: n => n.quantity },
    { header: 'Mkt Rate', value: n => n.mkt_rate },
    { header: 'Mkt Amt', value: n => n.mkt_amount },
    { header: 'Brok', value: n => n.brokerage },
    { header: 'STT', value: n => n.stt },
    { header: 'Ser Tax', value: n => n.ser_tax },
    { header: 'Sebi Tax', value: n => n.sebi_tax },
    { header: 'Turn Tax', value: n => n.turn_tax },
    { header: 'Stamp Duty', value: n => n.stamp_duty },
    { header: 'Net Rate', value: n => n.net_rate },
    { header: 'Net Amt', value: n => n.net_amt },
];

/**
 * The raw contract notes parsed from one uploaded file — shown in the notes
 * dialog on Manage Files and Contract Note Processing.
 */
const ContractNotesTable: React.FC<ContractNotesTableProps> = ({ notes, showAllocation = false }) => (
    <div className={styles.scroll}>
        <table className={shared.table}>
            <thead>
                <tr>
                    {showAllocation && <th>Sr. No.</th>}
                    {showAllocation && <th>Status</th>}
                    <th>Contract No</th>
                    <th>Party Code</th>
                    <th>ISIN</th>
                    <th>Series</th>
                    <th>Sett No</th>
                    <th>Side</th>
                    {NUMERIC_COLUMNS.map(c => (
                        <th key={c.header} className={shared['text-right']}>{c.header}</th>
                    ))}
                    <th>Imported At</th>
                </tr>
            </thead>
            <tbody>
                {notes.map((note, index) => (
                    <tr key={note.id}>
                        {showAllocation && <td>{index + 1}</td>}
                        {showAllocation && (
                            <td>
                                {note.is_allocated
                                    ? <Pill tone="positive" title="Allocated">Allocated</Pill>
                                    : <Pill tone="neutral" title="Not Allocated">Pending</Pill>}
                            </td>
                        )}
                        <td>{note.contract_no}</td>
                        <td>{note.party_code_name}</td>
                        <td>{note.company_isin}</td>
                        <td>{note.series}</td>
                        <td>{note.sett_no}</td>
                        <td>
                            <SideTag side={note.sell_buy === 1 ? 'buy' : 'sell'}>{note.sell_buy_display}</SideTag>
                        </td>
                        {NUMERIC_COLUMNS.map(c => (
                            <td key={c.header} className={shared['text-right']}>{c.value(note)}</td>
                        ))}
                        <td>{note.created_ts ? new Date(note.created_ts).toLocaleString() : '-'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export default ContractNotesTable;
